import React, { FC, createContext, useContext, useState, useCallback, BaseSyntheticEvent } from 'react'

import { isFirefox, isMobileSafari, isSafari, isIOS, isMacOs } from 'react-device-detect'
import { FormProvider, useForm } from 'react-hook-form'

import { AppContext } from 'Const'
import { PaymentGateways } from 'Types'
import inquiry, { Inquiry, useInquiry } from 'Utils/inquiry'

import { FormState } from '..'
import { StepContext } from '../..'
import PaymentForm from '../PaymentForm'

interface Gateway {
  applePay: boolean
  available: boolean
  field: `${PaymentGateways}Payment` | ''
  googlePay: boolean
  isSpreedly: boolean
  isSquare: boolean
  isStripe: boolean
  name: PaymentGateways | ''
}

interface PaymentContext {
  checkout(e?: BaseSyntheticEvent): Promise<void>
  errorMessage?: string
  gateway: Gateway
  paymentStatus: Inquiry<PaymentStatus>
  setErrorMessage(errorMessage: string): void
  setPaymentStatus(status: PaymentStatus): void
  setTokenizing(tokenizing: boolean): void
  tokenizing: boolean
}

export type PaymentStatus = 'error' | 'loading' | 'ready'

export const PaymentContext = createContext<PaymentContext>({
  gateway: {
    applePay: false,
    available: false,
    googlePay: false,
    isStripe: false,
    isSquare: false,
    isSpreedly: false,
    name: '',
    field: '',
  },
  checkout: () => Promise.resolve(),
  setErrorMessage: () => {},
  paymentStatus: inquiry<PaymentStatus>('loading'),
  setPaymentStatus: () => {},
  tokenizing: false,
  setTokenizing: () => {},
})

const PaymentProvider: FC = ({ children }) => {
  const { shop } = useContext(AppContext)
  const { nextStep } = useContext(StepContext)
  const form = useForm<FormState>()
  const { handleSubmit } = form

  const {
    isPaymentRequired,
    isSquarePaymentsAvailable,
    isSquareApplePayAvailable,
    isSpreedlyPaymentsAvailable,
    isStripePaymentsAvailable,
    checkoutSetting,
  } = shop
  const { enableApplePay, enableGooglePay, enableMarketplace } = checkoutSetting

  const isStripe = isStripePaymentsAvailable && isPaymentRequired
  const isSquare = isSquarePaymentsAvailable && isPaymentRequired
  const isSpreedly = isSpreedlyPaymentsAvailable && isPaymentRequired

  const isAppleOS = isIOS || isMacOs
  const isSafariBrowser = isSafari || isMobileSafari
  const isApplePayEnvironment = isAppleOS && isSafariBrowser
  const isGooglePayEnvironment = true

  const name = isStripe
    ? PaymentGateways.STRIPE
    : isSquare
    ? PaymentGateways.SQUARE
    : isSpreedly
    ? PaymentGateways.SPREEDLY
    : ''

  const gateway: Gateway = {
    available: isStripe || isSquare || isSpreedly,
    applePay: enableApplePay && isApplePayEnvironment,
    googlePay: enableGooglePay && isGooglePayEnvironment,
    isStripe,
    isSquare,
    isSpreedly,
    name,
    field: name ? `${name}Payment` : '',
  }

  if (isStripe) {
    gateway.googlePay &&= !isFirefox
  } else if (isSquare) {
    gateway.applePay &&= isSquareApplePayAvailable && !enableMarketplace
    gateway.googlePay &&= !enableMarketplace
  } else if (isSpreedly) {
    // TODO: update spreedly gateway in SCVR-529
    gateway.applePay = false
    gateway.googlePay = false
  }

  const [__paymentStatus, __setPaymentStatus] = useState<PaymentStatus>(isPaymentRequired ? 'loading' : 'ready')
  const [errorMessage, setErrorMessage] = useState('')
  const paymentStatus = useInquiry(__paymentStatus)

  const [tokenizing, setTokenizing] = useState(false)

  const setPaymentStatus = useCallback(
    (paymentStatus: PaymentStatus) => __setPaymentStatus((status) => (status === 'error' ? status : paymentStatus)),
    [],
  )

  const checkout = handleSubmit((payload) => {
    if (isStripe) payload = { stripePayment: payload.stripePayment }
    else if (isSquare) payload = { squarePayment: payload.squarePayment }
    else if (isSpreedly) payload = { spreedlyPayment: payload.spreedlyPayment }
    else payload = {}
    nextStep(payload)
  }, console.error)

  return (
    <PaymentContext.Provider
      value={{
        gateway,
        checkout,
        errorMessage,
        setErrorMessage,
        paymentStatus,
        setPaymentStatus,
        tokenizing,
        setTokenizing,
      }}
    >
      <FormProvider {...form}>
        <PaymentForm>{children}</PaymentForm>
      </FormProvider>
    </PaymentContext.Provider>
  )
}

export const usePayment = (): PaymentContext => useContext(PaymentContext)
export default PaymentProvider
